import { createPortal } from "react-dom";

export type HolidayLessonKind = "share" | "face" | "voice" | "cast" | "ai" | "meeting";

function Art({ kind }: { kind: HolidayLessonKind }) {
  if (kind === "share") {
    return (
      <svg className="hlm-art" viewBox="0 0 320 92" aria-hidden>
        <rect x="8" y="18" width="72" height="56" rx="8" fill="#163038" stroke="#4aa89c" />
        <text x="44" y="52" textAnchor="middle" fill="#8ff0dc" fontSize="11" fontWeight="800">in</text>
        <rect x="92" y="10" width="72" height="56" rx="8" fill="#1a2c38" stroke="#5aa7c8">
          <animate attributeName="y" values="10;16;10" dur="2.8s" repeatCount="indefinite" />
        </rect>
        <circle cx="128" cy="32" r="10" fill="#7eb8b1" />
        <rect x="176" y="22" width="72" height="56" rx="8" fill="#241a1a" stroke="#c45c4a" />
        <text x="212" y="54" textAnchor="middle" fill="#ffb4ac" fontSize="9" fontWeight="700">OOO</text>
        <path d="M80 46h16M164 46h16" stroke="#66d5c0" strokeWidth="2">
          <animate attributeName="stroke-opacity" values="0.2;1;0.2" dur="1.6s" repeatCount="indefinite" />
        </path>
        <circle cx="292" cy="46" r="18" fill="#102026" stroke="#8ff0dc" strokeWidth="2" />
        <circle cx="292" cy="40" r="6" fill="#8ff0dc" />
        <path d="M280 62c4-8 20-8 24 0" fill="none" stroke="#8ff0dc" />
      </svg>
    );
  }
  if (kind === "face") {
    return (
      <svg className="hlm-art" viewBox="0 0 320 92" aria-hidden>
        {[0, 1, 2].map((i) => (
          <rect key={i} x={12 + i * 54} y="16" width="46" height="58" rx="6" fill="#1a3038" stroke="#4aa89c" opacity={0.55 + i * 0.15} />
        ))}
        <path d="M168 46h28" stroke="#66d5c0" strokeWidth="2" />
        <ellipse cx="248" cy="46" rx="28" ry="32" fill="#163038" stroke="#8ff0dc" />
        <circle cx="238" cy="40" r="4" fill="#cfe8d1" />
        <circle cx="258" cy="40" r="4" fill="#cfe8d1" />
        <path d="M236 58c8 8 16 8 24 0" fill="none" stroke="#cfe8d1" />
        <rect x="232" y="8" width="32" height="10" rx="4" fill="#c45c4a">
          <animate attributeName="opacity" values="0.4;1;0.4" dur="1.4s" repeatCount="indefinite" />
        </rect>
        <text x="248" y="16" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="800">AI</text>
      </svg>
    );
  }
  if (kind === "voice") {
    return (
      <svg className="hlm-art" viewBox="0 0 320 92" aria-hidden>
        <rect x="10" y="20" width="120" height="52" rx="8" fill="#122026" stroke="#2a4450" />
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <rect key={i} x={22 + i * 13} y="36" width="7" height="22" rx="2" fill="#66d5c0">
            <animate attributeName="height" values="10;28;10" dur={`${0.7 + i * 0.11}s`} repeatCount="indefinite" />
            <animate attributeName="y" values="46;28;46" dur={`${0.7 + i * 0.11}s`} repeatCount="indefinite" />
          </rect>
        ))}
        <path d="M138 46h28" stroke="#66d5c0" strokeWidth="2" />
        <rect x="174" y="20" width="136" height="52" rx="8" fill="#241414" stroke="#c45c4a" />
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <rect key={i} x={186 + i * 14} y="36" width="7" height="22" rx="2" fill="#ffb4ac">
            <animate attributeName="height" values="8;26;8" dur={`${0.55 + i * 0.09}s`} repeatCount="indefinite" />
            <animate attributeName="y" values="48;30;48" dur={`${0.55 + i * 0.09}s`} repeatCount="indefinite" />
          </rect>
        ))}
      </svg>
    );
  }
  if (kind === "cast") {
    return (
      <svg className="hlm-art" viewBox="0 0 320 92" aria-hidden>
        {["MW", "SL", "JR"].map((label, i) => (
          <g key={label} transform={`translate(${18 + i * 102} 14)`}>
            <rect width="88" height="64" rx="8" fill="#122026" stroke={i === 2 ? "#44555c" : "#4aa89c"} />
            {i === 2 ? (
              <text x="44" y="38" textAnchor="middle" fill="#8aa2a8" fontSize="16" fontWeight="800">{label}</text>
            ) : (
              <circle cx="44" cy="28" r="14" fill="#2a5a55" />
            )}
            <rect x="22" y="48" width="44" height="10" rx="4" fill="#c45c4a">
              <animate attributeName="opacity" values="0.35;1;0.35" dur={`${1.2 + i * 0.3}s`} repeatCount="indefinite" />
            </rect>
            <text x="44" y="56" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="800">FAKE</text>
          </g>
        ))}
      </svg>
    );
  }
  if (kind === "ai") {
    return (
      <svg className="hlm-art" viewBox="0 0 320 92" aria-hidden>
        {["Hear", "Parse", "Reply", "Speak"].map((label, i) => (
          <g key={label} transform={`translate(${12 + i * 78} 22)`}>
            <circle cx="28" cy="24" r="22" fill="#163038" stroke="#66d5c0" />
            <text x="28" y="28" textAnchor="middle" fill="#8ff0dc" fontSize="9" fontWeight="800">{label}</text>
            {i < 3 && <path d="M54 24h16" stroke="#66d5c0" strokeWidth="2" />}
          </g>
        ))}
        <rect x="96" y="70" width="128" height="8" rx="4" fill="#2f6b61">
          <animate attributeName="width" values="32;128;32" dur="2.4s" repeatCount="indefinite" />
        </rect>
      </svg>
    );
  }
  return (
    <svg className="hlm-art" viewBox="0 0 320 92" aria-hidden>
      {[0, 1, 2, 3].map((i) => (
        <g key={i} transform={`translate(${16 + (i % 2) * 150} ${8 + Math.floor(i / 2) * 42})`}>
          <rect width="138" height="36" rx="6" fill="#122026" stroke="#4aa89c" />
          <circle cx="22" cy="18" r="10" fill="#2a5a55" />
          <rect x="40" y="12" width="70" height="6" rx="3" fill="#8aa2a8" />
          <rect x="40" y="22" width="46" height="5" rx="2" fill="#2f6b61" />
          <rect x="108" y="8" width="22" height="8" rx="3" fill="#c45c4a">
            <animate attributeName="opacity" values="0.4;1;0.4" dur={`${1 + i * 0.25}s`} repeatCount="indefinite" />
          </rect>
        </g>
      ))}
    </svg>
  );
}

export function HolidayLessonStrip({ kind }: { kind: HolidayLessonKind }) {
  return (
    <div className="hlm-strip" aria-hidden>
      <Art kind={kind} />
    </div>
  );
}

export function HolidayLessonModal({
  kind,
  title,
  paragraphs,
  onClose,
  soft,
}: {
  kind: HolidayLessonKind;
  title: string;
  paragraphs: string[];
  onClose: () => void;
  soft?: boolean;
}) {
  return createPortal(
    <div className={`hlm-back${soft ? " is-soft" : ""}`} onClick={onClose} role="presentation">
      <div
        className={`hlm-card${kind === "meeting" ? " hlm-wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="hlm-title"
        onClick={(event) => event.stopPropagation()}
      >
        <Art kind={kind} />
        <small>LESSON</small>
        <h2 id="hlm-title">{title}</h2>
        <div className="hlm-copy">
          {paragraphs.map((paragraph) => (
            <p key={paragraph.slice(0, 40)}>{paragraph}</p>
          ))}
        </div>
        <button type="button" className="ht-primary" onClick={onClose}>
          Got it
        </button>
      </div>
    </div>,
    document.body,
  );
}
